import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { SnackBarComponent } from '../../../../components/snackbar/snackbar.component';
import { StripeService } from '../../../../services/stripe/stripe.service';
import { UsersService } from '../../../../services/users/users.service';

type SubscriptionDunningItemStatus =
  | 'active'
  | 'payment_failed'
  | 'payment_retry'
  | 'cancellation_pending'
  | 'cancelled';

type SubscriptionDunningFilterState =
  | ''
  | 'non_active'
  | SubscriptionDunningItemStatus;

interface SubscriptionDunningItem {
  userId: string;
  userName: string;
  userEmail: string;
  vendorProductId: string;
  vendorName: string;
  productName: string;
  productDisplayName: string;
  status: SubscriptionDunningItemStatus;
  statusLabel: string;
  retryCount: number;
  retryTotal: number;
  nextRetryAt: string;
  cancelAt: string;
  canceledAt: string;
  amountDue: number | null;
  paymentMethodLabel: string;
  paymentTypeLabel: string;
  stripeSubscriptionId: string;
  stripeStatus: string;
  stateUpdatedAt: string;
  updatedAt: string;
}

@Component({
    selector: 'app-superadmin-users-subscription-dunning',
    templateUrl: './superadmin-users-subscription-dunning.component.html',
    styleUrls: ['./superadmin-users-subscription-dunning.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SuperadminUsersSubscriptionDunningComponent implements OnInit, OnDestroy {
  public items = new MatTableDataSource<SubscriptionDunningItem>([]);
  public columnsToDisplay: string[] = [
    'updated',
    'user',
    'email',
    'product',
    'state',
    'retry',
    'nextRetry',
    'cancellationDate',
    'amountDue',
    'paymentSource',
    'paymentDetails',
    'actions',
  ];
  public searchValue = '';
  public selectedState: SubscriptionDunningFilterState = 'non_active';
  public loading = false;
  public stateOptions: { label: string; value: SubscriptionDunningFilterState }[] = [
    { label: 'All', value: '' },
    { label: 'Non-Active', value: 'non_active' },
    { label: 'Active', value: 'active' },
    { label: 'Payment Failed', value: 'payment_failed' },
    { label: 'Payment Retry', value: 'payment_retry' },
    { label: 'Cancellation Pending', value: 'cancellation_pending' },
    { label: 'Cancelled', value: 'cancelled' },
  ];

  private page = 0;
  private readonly limit = 25;
  private endOfList = false;
  private pendingRefresh = false;
  private requestVersion = 0;
  private readonly searchDebounceMs = 300;
  private readonly filterValueChanges = new Subject<string>();
  private readonly filterSubscription: Subscription;
  private readonly defaultState: SubscriptionDunningFilterState = 'non_active';
  private readonly triggeringSubscriptionIds = new Set<string>();

  constructor(
    private usersService: UsersService,
    private stripeService: StripeService,
    private snackBar: MatSnackBar,
  ) {
    this.filterSubscription = this.filterValueChanges
      .pipe(
        debounceTime(this.searchDebounceMs),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadItems(true));
  }

  ngOnInit(): void {
    this.loadItems(true);
  }

  ngOnDestroy(): void {
    this.filterSubscription.unsubscribe();
  }

  async loadItems(clear = false): Promise<void> {
    if (clear) {
      this.page = 0;
      this.endOfList = false;
      this.items.data = [];
      this.requestVersion += 1;
      if (this.loading) {
        this.pendingRefresh = true;
        return;
      }
    }
    if (this.loading || this.endOfList) {
      return;
    }

    this.loading = true;
    const requestVersion = this.requestVersion;
    try {
      const result = await this.usersService.getSubscriptionDunning(
        this.searchValue,
        this.selectedState,
        this.page,
        this.limit,
      );
      const rows = Array.isArray(result) ? result : Array.isArray((result as any)?.data) ? (result as any).data : [];
      if (requestVersion !== this.requestVersion) {
        return;
      }
      if (rows.length === 0) {
        this.endOfList = true;
      } else {
        this.items.data = this.items.data.concat(rows);
        this.page += 1;
      }
    } catch (error) {
      console.error('Error loading subscription dunning items', error);
    } finally {
      this.loading = false;
      if (this.pendingRefresh) {
        this.pendingRefresh = false;
        this.loadItems(true);
      }
    }
  }

  queueFilters(): void {
    this.filterValueChanges.next(
      JSON.stringify({
        searchValue: this.searchValue,
        selectedState: this.selectedState,
      }),
    );
  }

  clearFilters(): void {
    this.searchValue = '';
    this.selectedState = this.defaultState;
    this.loadItems(true);
  }

  onScroll(event: any): void {
    const scrollTop = event.target.scrollTop;
    const scrollHeight = event.target.scrollHeight;
    const offsetHeight = event.target.offsetHeight;
    if (scrollTop + offsetHeight >= scrollHeight - 150) {
      this.loadItems();
    }
  }

  getUpdatedAt(item: SubscriptionDunningItem): string {
    return item.updatedAt || item.stateUpdatedAt || '';
  }

  getCancellationDate(item: SubscriptionDunningItem): string {
    return item.cancelAt || item.canceledAt || '';
  }

  getProductLabel(item: SubscriptionDunningItem): string {
    return item.productDisplayName || item.productName || '-';
  }

  getRetryLabel(item: SubscriptionDunningItem): string {
    if (!item.retryCount && !item.retryTotal) {
      return '-';
    }
    return `${item.retryCount || 0} / ${item.retryTotal || 0}`;
  }

  getStatusChipClass(item: SubscriptionDunningItem): string {
    switch (item.status) {
      case 'active':
        return 'status-chip-active';
      case 'payment_failed':
        return 'status-chip-payment-failed';
      case 'payment_retry':
        return 'status-chip-payment-retry';
      case 'cancellation_pending':
        return 'status-chip-cancellation-pending';
      case 'cancelled':
        return 'status-chip-cancelled';
      default:
        return '';
    }
  }

  canTriggerBillingCycle(item: SubscriptionDunningItem): boolean {
    return !!item.stripeSubscriptionId && !this.triggeringSubscriptionIds.has(item.stripeSubscriptionId);
  }

  async triggerBillingCycle(item: SubscriptionDunningItem): Promise<void> {
    const subscriptionId = item.stripeSubscriptionId;
    if (!subscriptionId || this.triggeringSubscriptionIds.has(subscriptionId)) {
      return;
    }

    this.triggeringSubscriptionIds.add(subscriptionId);
    try {
      await this.stripeService.triggerSubscriptionBillingCycle(subscriptionId);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: 'Billing cycle triggered',
      });
      this.loadItems(true);
    } catch (error: any) {
      console.error('Error triggering billing cycle', error);
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: error?.error?.message || 'Unable to trigger billing cycle.',
      });
    } finally {
      this.triggeringSubscriptionIds.delete(subscriptionId);
    }
  }
}
